import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Check, X, Clock, Settings2, Info, ToggleLeft, ToggleRight, Plus, Search, Filter, LayoutGrid, List, User, CalendarDays, FileText, Loader2, AlertCircle, ChevronDown, RefreshCw } from 'lucide-react';
import { LeaveType } from '../types';
import leaveService, { Leave, LeaveCreateDto } from '../services/leaveService';
import employeeService, { Employee } from '../services/employeeService';
import leaveTypesService, { LeaveType as LeaveTypeDto } from '../services/leaveTypesService';
import { useAppContext } from '../context/AppContext';
import { Role } from '../types';
import { uyar, hata, onayla } from '../components/GlobalModal';

const Leaves = () => {
  const { t } = useTranslation();
  const { currentUser } = useAppContext();
  const isAdmin = currentUser?.role === Role.ADMIN;

  const leaveTypeLabels: Record<string, string> = {
    'Annual': t('leaves.annual'),
    'Yıllık İzin': t('leaves.annual'),
    'Sick': t('leaves.sick'),
    'Hastalık İzni': t('leaves.sick'),
    'Unpaid': t('leaves.unpaid'),
    'Ücretsiz İzin': t('leaves.unpaid'),
    'Maternity': t('leaves.maternity'),
    'Doğum İzni': t('leaves.maternity'),
    'Excuse': t('leaves.excuse'),
    'Mazeret İzni': t('leaves.excuse'),
    'Hourly': 'Saatlik İzin',
    'Saatlik İzin': 'Saatlik İzin',
    // 2026-08-15: Eski kayitlarda Ingilizce kalanlar
    'Paternity': 'Babalık İzni',
    'Marriage': 'Evlilik İzni',
    'Bereavement': 'Ölüm İzni',
  };

  // 2026-08-14: Saatlik izin ARTIK bir izin turu. Kullanici sure olarak
  // "Saatlik" secince tur de otomatik buna donusur; listede "Yillik Izin"
  // gorunup kafa karistirmasin diye.
  const SAATLIK_IZIN = 'Saatlik İzin';

  const [dbLeaveTypes, setDbLeaveTypes] = useState<LeaveTypeDto[]>([]);

  // 2026-08-15 (musteri: "Annual yaziyor, Turkcelestirir misiniz"):
  // Buradaki value DB'ye AYNEN yaziliyordu; puantaj/Excel/PDF ciktilarinda
  // "Annual" gibi Ingilizce kodlar goruluyordu. Artik kaydedilen deger Turkce,
  // ekranda gorunen etiket ise secili dile gore ceviriliyor.
  // (Eski kayitlardaki Ingilizce degerler TimesheetService.IzinTuruTurkce ile
  //  gosterim aninda cevriliyor — veri tasima gerekmiyor.)
  const staticLeaveTypeOptions = [
    { value: 'Yıllık İzin', label: t('leaves.annual') },
    { value: 'Hastalık İzni', label: t('leaves.sick') },
    { value: 'Ücretsiz İzin', label: t('leaves.unpaid') },
    { value: 'Doğum İzni', label: t('leaves.maternity') },
    { value: 'Babalık İzni', label: 'Babalık İzni' },
    { value: 'Evlilik İzni', label: 'Evlilik İzni' },
    { value: 'Ölüm İzni', label: 'Ölüm İzni' },
    { value: 'Mazeret İzni', label: t('leaves.excuse') },
  ];

  const leaveTypeOptions = dbLeaveTypes.length > 0
    ? dbLeaveTypes.map(lt => ({ value: lt.typeName, label: lt.typeName }))
    : staticLeaveTypeOptions;

  const statusLabels: Record<string, string> = {
    'Pending': t('leaves.pending'),
    'Approved': t('leaves.approved'),
    'Rejected': t('leaves.rejected'),
    'Cancelled': t('leaves.cancelled'),
  };

  const leaveRuleLabels: Record<string, string> = {
    'Annual': t('leaves.annual'),
    'Sick': t('leaves.sick'),
    'Unpaid': t('leaves.unpaid'),
    'Maternity': t('leaves.maternity'),
    'Excuse': t('leaves.excuse'),
  };

  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showRulesConfig, setShowRulesConfig] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const [newLeave, setNewLeave] = useState<LeaveCreateDto>({
    employeeId: 0,
    leaveType: 'Yıllık İzin',
    startDate: '',
    endDate: '',
    reason: '',
    isHourly: false,
    startTime: '',
    endTime: '',
  } as any);

  // Anahtarlar hem Ingilizce (eski kayitlar) hem Turkce (yeni kayitlar) —
  // leaveRules[leave.leaveType] her iki durumda da dogru sonucu versin.
  const [leaveRules, setLeaveRules] = useState<Record<string, boolean>>({
    'Annual': true,           'Yıllık İzin': true,
    'Sick': false,            'Hastalık İzni': false,
    'Unpaid': false,          'Ücretsiz İzin': false,
    'Maternity': false,       'Doğum İzni': false,
    'Excuse': true,           'Mazeret İzni': true,
    'Babalık İzni': false,    'Evlilik İzni': false,
    'Ölüm İzni': false,
  });

  useEffect(() => {
    fetchData();
    (async () => {
      try {
        const r = await leaveTypesService.list();
        if (r?.success && Array.isArray(r.data)) setDbLeaveTypes(r.data);
      } catch {}
    })();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [leavesRes, employeesRes] = await Promise.all([
        leaveService.getAll(),
        employeeService.getAll(),
      ]);

      if (leavesRes.success && leavesRes.data) {
        setLeaves(leavesRes.data);
      }
      if (employeesRes.success && employeesRes.data) {
        setEmployees(employeesRes.data);
      }
    } catch (err) {
      setError(t('leaves.loadError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeName = (employeeId: number) => {
    const emp = employees.find(e => e.id === employeeId || e.employeeId === employeeId);
    return emp ? emp.fullName || `${emp.firstName} ${emp.lastName}` : t('leaves.unknown');
  };

  const calculateDays = (start: string, end: string) => {
    const s = new Date(start);
    const e = new Date(end);
    const diffTime = Math.abs(e.getTime() - s.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const handleApprove = async (leave: Leave) => {
    setActionLoading(leave.id);
    try {
      const result = await leaveService.approve(leave.id);
      if (result.success) {
        setLeaves(prev => prev.map(l =>
          l.id === leave.id ? { ...l, status: 'Approved' } : l
        ));
      } else {
        uyar(result.message || t('leaves.approveFailed'));
      }
    } catch (err) {
      uyar(t('leaves.approveError'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (leave: Leave) => {
    const reason = window.prompt(t('leaves.rejectReason'));
    setActionLoading(leave.id);
    try {
      const result = await leaveService.reject(leave.id, reason || undefined);
      if (result.success) {
        setLeaves(prev => prev.map(l =>
          l.id === leave.id ? { ...l, status: 'Rejected' } : l
        ));
      } else {
        uyar(result.message || t('leaves.rejectFailed'));
      }
    } catch (err) {
      uyar(t('leaves.rejectError'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (leave: Leave) => {
    if (!(await onayla(t('leaves.cancelConfirm')))) return;

    setActionLoading(leave.id);
    try {
      const result = await leaveService.cancel(leave.id);
      if (result.success) {
        setLeaves(prev => prev.map(l =>
          l.id === leave.id ? { ...l, status: 'Cancelled' } : l
        ));
      } else {
        uyar(result.message || t('leaves.cancelFailed'));
      }
    } catch (err) {
      uyar(t('leaves.cancelError'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (leave: Leave) => {
    if (!(await onayla(t('leaves.deleteConfirm')))) return;

    setActionLoading(leave.id);
    try {
      const result = await leaveService.delete(leave.id);
      if (result.success) {
        setLeaves(prev => prev.filter(l => l.id !== leave.id));
      } else {
        uyar(result.message || t('leaves.deleteFailed'));
      }
    } catch (err) {
      uyar(t('leaves.deleteError'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateLeave = async () => {
    if (!newLeave.employeeId) {
      uyar(t('leaves.selectEmployee'));
      return;
    }
    if (!newLeave.startDate || !newLeave.endDate) {
      uyar(t('leaves.selectDateRange'));
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(newLeave.startDate);
    const endDate = new Date(newLeave.endDate);

    if (!isAdmin && startDate < today) {
      uyar(t('leaves.startDatePast'));
      return;
    }
    if (!isAdmin && endDate < today) {
      uyar(t('leaves.endDatePast'));
      return;
    }
    if (startDate > endDate) {
      uyar(t('leaves.startAfterEnd'));
      return;
    }

    setActionLoading(-1);
    try {
      const result = await leaveService.create(newLeave);
      if (result.success) {
        setShowNewModal(false);
        setNewLeave({
          employeeId: 0,
          leaveType: 'Yıllık İzin',
          startDate: '',
          endDate: '',
          reason: '',
          isHourly: false,
          startTime: '',
          endTime: '',
        } as any);
        fetchData();
      } else {
        uyar(result.message || t('leaves.createFailed'));
      }
    } catch (err) {
      uyar(t('leaves.createError'));
    } finally {
      setActionLoading(null);
    }
  };

  const filteredLeaves = leaves.filter(leave => {
    const employeeName = leave.employeeName || getEmployeeName(leave.employeeId);
    const matchesSearch = employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          leave.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || leave.status === statusFilter;
    const matchesType = typeFilter === 'all' || leave.leaveType === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const stats = {
    total: leaves.length,
    pending: leaves.filter(l => l.status === 'Pending').length,
    approved: leaves.filter(l => l.status === 'Approved').length,
    rejected: leaves.filter(l => l.status === 'Rejected').length,
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Approved':
        return 'bg-green-50 text-green-600 border-green-200';
      case 'Pending':
        return 'bg-amber-50 text-amber-600 border-amber-200';
      case 'Rejected':
        return 'bg-red-50 text-red-600 border-red-200';
      case 'Cancelled':
        return 'bg-slate-50 text-slate-600 border-slate-200';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">{t('common.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="w-12 h-12 text-red-500" />
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {t('leaves.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{t('leaves.title')}</h1>
          <p className="text-slate-500 text-sm">{t('leaves.subtitle')}</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={fetchData}
            className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            title={t('leaves.refresh')}
          >
            <RefreshCw size={18} />
          </button>
          <button
            onClick={() => setShowRulesConfig(!showRulesConfig)}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors border ${
              showRulesConfig ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
          >
            <Settings2 size={18} /> {t('leaves.leaveRules')}
          </button>
          <button
            onClick={() => setShowNewModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm flex items-center gap-2"
          >
            <Plus size={18} /> {t('leaves.requestLeave')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
          <div className="text-sm text-slate-500">{t('leaves.totalRequests')}</div>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
          <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
          <div className="text-sm text-amber-600">{t('leaves.pending')}</div>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
          <div className="text-sm text-green-600">{t('leaves.approved')}</div>
        </div>
        <div className="bg-red-50 rounded-xl p-4 border border-red-200">
          <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
          <div className="text-sm text-red-600">{t('leaves.rejected')}</div>
        </div>
      </div>

      {/* Leave Rules Panel */}
      {showRulesConfig && (
        <div className="bg-white rounded-xl shadow-md border border-indigo-100 p-6 animate-in slide-in-from-top duration-300">
          <div className="flex items-center gap-2 mb-4">
            <Settings2 size={20} className="text-indigo-600" />
            <h3 className="font-bold text-slate-800">{t('leaves.balanceDeductionRules')}</h3>
            <span className="ml-auto text-xs font-medium text-slate-400">{t('leaves.whichTypesDeducted')}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {Object.entries(leaveRules).map(([type, deducts]) => (
              <div
                key={type}
                className={`p-3 rounded-lg border flex flex-col gap-3 transition-all ${
                  deducts ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-100'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-slate-700">{leaveRuleLabels[type] || type}</span>
                  <button
                    onClick={() => setLeaveRules(prev => ({ ...prev, [type]: !deducts }))}
                    className={`transition-colors ${deducts ? 'text-indigo-600' : 'text-slate-300'}`}
                  >
                    {deducts ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                  </button>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider">
                  {deducts ? (
                    <span className="text-indigo-600 flex items-center gap-1">
                      <Check size={10} /> {t('leaves.deducted')}
                    </span>
                  ) : (
                    <span className="text-slate-400 flex items-center gap-1">
                      <X size={10} /> {t('leaves.notDeducted')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100 flex items-start gap-2">
            <Info size={16} className="text-amber-600 mt-0.5" />
            <p className="text-xs text-amber-700 leading-relaxed">
              {t('leaves.balanceDeductionInfo')}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex gap-3 flex-1 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder={t('leaves.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
              >
                <option value="all">{t('leaves.allStatuses')}</option>
                <option value="Pending">{t('leaves.pending')}</option>
                <option value="Approved">{t('leaves.approved')}</option>
                <option value="Rejected">{t('leaves.rejected')}</option>
                <option value="Cancelled">{t('leaves.cancelled')}</option>
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            <div className="relative">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
              >
                <option value="all">{t('leaves.allLeaveTypes')}</option>
                {leaveTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div className="flex items-center gap-2 border border-slate-200 rounded-lg p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded ${viewMode === 'card' ? 'bg-blue-100 text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
              title={t('leaves.cardView')}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
              title={t('leaves.listView')}
            >
              <List size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">{t('leaves.employee')}</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">{t('leaves.leaveType')}</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">{t('leaves.dateRange')}</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">{t('leaves.days')}</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">{t('leaves.description')}</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">{t('leaves.status')}</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">{t('leaves.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLeaves.map(leave => {
                  const days = leave.daysCount || calculateDays(leave.startDate, leave.endDate);
                  const isDeductible = leaveRules[leave.leaveType];

                  return (
                    <tr key={leave.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">
                            {(leave.employeeName || getEmployeeName(leave.employeeId)).split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <span className="font-medium text-slate-800">{leave.employeeName || getEmployeeName(leave.employeeId)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">{leaveTypeLabels[leave.leaveType] || leave.leaveType}</span>
                        {(leave as any).isHourly && (
                          <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">
                            {(leave as any).startTime && (leave as any).endTime
                              ? `${String((leave as any).startTime).substring(0, 5)}-${String((leave as any).endTime).substring(0, 5)}`
                              : 'SAATLİK'}
                          </span>
                        )}
                        {isDeductible && (
                          <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded">{t('leaves.deducted')}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {formatDate(leave.startDate)} - {formatDate(leave.endDate)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-bold text-slate-800">{days}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500 max-w-xs truncate">
                        {leave.reason || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold border ${getStatusBadge(leave.status)}`}>
                          {statusLabels[leave.status] || leave.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {leave.status === 'Pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(leave)}
                                disabled={actionLoading === leave.id}
                                className="p-1.5 bg-green-50 text-green-600 rounded hover:bg-green-100 border border-green-200 disabled:opacity-50"
                                title={t('leaves.approve')}
                              >
                                {actionLoading === leave.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                              </button>
                              <button
                                onClick={() => handleReject(leave)}
                                disabled={actionLoading === leave.id}
                                className="p-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100 border border-red-200 disabled:opacity-50"
                                title={t('leaves.reject')}
                              >
                                <X size={14} />
                              </button>
                            </>
                          )}
                          {leave.status === 'Approved' && (
                            <button
                              onClick={() => handleCancel(leave)}
                              disabled={actionLoading === leave.id}
                              className="p-1.5 bg-slate-50 text-slate-600 rounded hover:bg-slate-100 border border-slate-200 disabled:opacity-50"
                              title={t('leaves.cancelLeave')}
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredLeaves.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      {t('leaves.noRecords')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Card View */}
      {viewMode === 'card' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center">
            <h2 className="font-bold text-lg text-slate-800">{t('leaves.leaveRequests')}</h2>
            <div className="flex gap-2">
              <span className="text-xs font-medium px-2 py-1 bg-slate-100 rounded text-slate-600">{t('leaves.totalCount', { count: filteredLeaves.length })}</span>
              <span className="text-xs font-medium px-2 py-1 bg-amber-50 rounded text-amber-600">{t('leaves.pendingCount', { count: filteredLeaves.filter(l => l.status === 'Pending').length })}</span>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {filteredLeaves.map(leave => {
              const days = leave.daysCount || calculateDays(leave.startDate, leave.endDate);
              const isDeductible = leaveRules[leave.leaveType];

              return (
                <div key={leave.id} className="p-6 transition-colors hover:bg-slate-50">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                        <Calendar size={20} />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-800">{leave.employeeName || getEmployeeName(leave.employeeId)}</h4>
                        <div className="text-sm text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-700 px-2 py-0.5 bg-slate-100 rounded text-xs border border-slate-200">
                            {leaveTypeLabels[leave.leaveType] || leave.leaveType}
                            {(leave as any).isHourly && (leave as any).startTime && (leave as any).endTime && (
                              <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">
                                {String((leave as any).startTime).substring(0, 5)}-{String((leave as any).endTime).substring(0, 5)}
                              </span>
                            )}
                          </span>
                          <span>{formatDate(leave.startDate)} <span className="text-slate-400">-</span> {formatDate(leave.endDate)}</span>
                          <span className="text-xs text-slate-400">({t('leaves.daysCount', { count: days })})</span>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            isDeductible ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400'
                          }`}>
                            {isDeductible ? t('leaves.deducted') : t('leaves.notDeducted')}
                          </span>
                        </div>
                        {leave.reason && (
                          <p className="text-sm text-slate-500 mt-2 italic">
                            "{leave.reason}"
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-3">
                      {leave.status === 'Approved' ? (
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border border-green-100">
                            <Check size={12} /> {t('leaves.approved')}
                          </span>
                          <button
                            onClick={() => handleCancel(leave)}
                            disabled={actionLoading === leave.id}
                            className="p-2 bg-slate-50 text-slate-600 rounded-lg hover:bg-slate-100 border border-slate-200 transition-colors disabled:opacity-50"
                            title={t('leaves.cancelLeave')}
                          >
                            {actionLoading === leave.id ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
                          </button>
                        </div>
                      ) : leave.status === 'Pending' ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleApprove(leave)}
                            disabled={actionLoading === leave.id}
                            className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 border border-green-200 transition-colors shadow-sm disabled:opacity-50"
                            title={t('leaves.approve')}
                          >
                            {actionLoading === leave.id ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                          </button>
                          <button
                            onClick={() => handleReject(leave)}
                            disabled={actionLoading === leave.id}
                            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 border border-red-200 transition-colors shadow-sm disabled:opacity-50"
                            title={t('leaves.reject')}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : leave.status === 'Rejected' ? (
                        <span className="text-red-500 bg-red-50 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border border-red-100">
                          {t('leaves.rejected')}
                        </span>
                      ) : (
                        <span className="text-slate-500 bg-slate-50 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border border-slate-100">
                          {t('leaves.cancelled')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredLeaves.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                {t('leaves.noRecords')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Leave Request Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowNewModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-800">{t('leaves.newLeaveRequest')}</h3>
              <p className="text-sm text-slate-500">{t('leaves.createLeaveSubtitle')}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('leaves.employee')} *</label>
                <select
                  value={newLeave.employeeId}
                  onChange={(e) => setNewLeave(prev => ({ ...prev, employeeId: parseInt(e.target.value) }))}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={0}>{t('leaves.selectEmployeePlaceholder')}</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.fullName || `${emp.firstName} ${emp.lastName}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('leaves.leaveType')} *</label>
                <select
                  value={newLeave.leaveType}
                  disabled={!!(newLeave as any).isHourly}
                  onChange={(e) => setNewLeave(prev => ({ ...prev, leaveType: e.target.value }))}
                  className={`w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    (newLeave as any).isHourly ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''
                  }`}
                >
                  {/* Saatlik izin turu listede her zaman bulunsun (DB'de yoksa bile) */}
                  {(leaveTypeOptions.some(o => o.value === SAATLIK_IZIN)
                    ? leaveTypeOptions
                    : [...leaveTypeOptions, { value: SAATLIK_IZIN, label: SAATLIK_IZIN }]
                  ).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  {(newLeave as any).isHourly
                    ? 'Süre olarak "Saatlik" seçildiği için izin türü otomatik ayarlandı.'
                    : <>İznin <strong>sebebi</strong>. Süresini aşağıdan seçin.</>}
                </p>
              </div>

              {/* 2026-08-14: Sure tipi ARTIK acik bir secim. Onceden sayfanin altinda
                  ayri bir "Saatlik izin" kutucugu vardi; kullanicilar tur listesinde
                  saatlik secenegini arayip bulamiyor, sonra baska bir tur secip
                  kafasi karisiyordu. Sebep (tur) ile sure tipi net ayrildi. */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">İzin Süresi *</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { hourly: false, baslik: 'Tam Gün', alt: 'Bir veya birden fazla gün' },
                    { hourly: true, baslik: 'Saatlik', alt: 'Aynı gün içinde saat aralığı' },
                  ].map(sec => {
                    const secili = !!(newLeave as any).isHourly === sec.hourly;
                    return (
                      <button
                        key={String(sec.hourly)}
                        type="button"
                        onClick={() => setNewLeave(prev => ({
                          ...prev,
                          isHourly: sec.hourly,
                          // Saatlik izin tek gunluktur: bitis = baslangic
                          endDate: sec.hourly && prev.startDate ? prev.startDate : prev.endDate,
                          // Tur de otomatik degissin — listede "Saatlik Izin" gorunsun.
                          // Tam Gun'e donulurse saatlik turden cikip normal turlere don.
                          leaveType: sec.hourly
                            ? SAATLIK_IZIN
                            : (prev.leaveType === SAATLIK_IZIN
                                ? (leaveTypeOptions.find(o => o.value !== SAATLIK_IZIN)?.value || 'Yıllık İzin')
                                : prev.leaveType),
                          ...(sec.hourly ? {} : { startTime: '', endTime: '' }),
                        } as any))}
                        className={`text-left px-4 py-3 rounded-xl border-2 transition-all ${
                          secili
                            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <div className={`text-sm font-bold ${secili ? 'text-blue-700' : 'text-slate-700'}`}>{sec.baslik}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{sec.alt}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className={(newLeave as any).isHourly ? '' : 'grid grid-cols-2 gap-4'}>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {(newLeave as any).isHourly ? 'İzin Tarihi *' : `${t('leaves.startDate')} *`}
                  </label>
                  <input
                    type="date"
                    value={newLeave.startDate}
                    min={isAdmin ? undefined : new Date().toISOString().split('T')[0]}
                    onChange={(e) => setNewLeave(prev => ({
                      ...prev,
                      startDate: e.target.value,
                      // Saatlik izinde bitis tarihi baslangic ile ayni kalmali
                      endDate: (prev as any).isHourly ? e.target.value : prev.endDate,
                    } as any))}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {isAdmin && (newLeave as any).isHourly && (
                    <p className="text-[10px] text-amber-600 mt-1">Admin modu: geçmişe yönelik izin girebilirsiniz</p>
                  )}
                </div>
                {/* Saatlik izinde bitis tarihi anlamsiz — gizleniyor, otomatik esitleniyor */}
                {!(newLeave as any).isHourly && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('leaves.endDate')} *</label>
                    <input
                      type="date"
                      value={newLeave.endDate}
                      min={isAdmin ? (newLeave.startDate || undefined) : (newLeave.startDate || new Date().toISOString().split('T')[0])}
                      onChange={(e) => setNewLeave(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {isAdmin && (
                      <p className="text-[10px] text-amber-600 mt-1">Admin modu: geçmişe yönelik izin girebilirsiniz</p>
                    )}
                  </div>
                )}
              </div>
              {newLeave.startDate && newLeave.endDate && new Date(newLeave.startDate) <= new Date(newLeave.endDate) && !(newLeave as any).isHourly && (
                <div className="text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
                  {t('leaves.totalDays', { days: calculateDays(newLeave.startDate, newLeave.endDate) })}
                </div>
              )}

              {(newLeave as any).isHourly && (
                <div className="border border-slate-200 rounded-xl p-3 bg-slate-50">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Başlangıç Saati</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={5}
                        placeholder="HH:mm"
                        value={(newLeave as any).startTime || ''}
                        onChange={e => {
                          let v = e.target.value.replace(/[^0-9:]/g, '').substring(0, 5);
                          const cur = (newLeave as any).startTime || '';
                          if (v.length === 2 && !v.includes(':') && cur.length !== 3) v += ':';
                          setNewLeave(prev => ({ ...prev, startTime: v } as any));
                        }}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-lg tracking-wider"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Bitiş Saati</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={5}
                        placeholder="HH:mm"
                        value={(newLeave as any).endTime || ''}
                        onChange={e => {
                          let v = e.target.value.replace(/[^0-9:]/g, '').substring(0, 5);
                          const cur = (newLeave as any).endTime || '';
                          if (v.length === 2 && !v.includes(':') && cur.length !== 3) v += ':';
                          setNewLeave(prev => ({ ...prev, endTime: v } as any));
                        }}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-lg tracking-wider"
                      />
                    </div>
                  </div>
                  {/* Girilen saat araliginin toplami — kullanici kaydetmeden once gorsun */}
                  {(() => {
                    const bas = (newLeave as any).startTime || '';
                    const bit = (newLeave as any).endTime || '';
                    const gecerli = (s: string) => /^\d{1,2}:\d{2}$/.test(s);
                    if (!gecerli(bas) || !gecerli(bit)) return null;
                    const [bh, bm] = bas.split(':').map(Number);
                    const [eh, em] = bit.split(':').map(Number);
                    const fark = (eh * 60 + em) - (bh * 60 + bm);
                    if (fark <= 0) {
                      return (
                        <div className="mt-3 text-xs flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-3 py-2 text-red-700">
                          <AlertCircle size={14} className="mt-0.5 shrink-0" />
                          <span>Bitiş saati başlangıç saatinden sonra olmalı.</span>
                        </div>
                      );
                    }
                    return (
                      <div className="mt-3 text-sm text-blue-700 bg-blue-50 border border-blue-200 px-3 py-2 rounded-lg">
                        Toplam <strong>{Math.floor(fark / 60)} saat {fark % 60} dk</strong> izin
                      </div>
                    );
                  })()}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('leaves.description')}</label>
                <textarea
                  value={newLeave.reason || ''}
                  onChange={(e) => setNewLeave(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  placeholder={t('leaves.reasonPlaceholder')}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => setShowNewModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateLeave}
                disabled={actionLoading === -1}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {actionLoading === -1 && <Loader2 size={16} className="animate-spin" />}
                {t('leaves.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaves;
